import React, { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { api, getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Loader,
  Droplets,
  Clock,
  Calendar,
  Zap,
  Gauge,
  Power,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
} from "lucide-react";

const IST_TIME_ZONE = "Asia/Kolkata";
const PDF_PAGE = { width: 841.89, height: 595.28, margin: 28 };
const NO_TOWER_ANALYTICS_ID = "__no_tower__";

const sanitizeFileName = (value) =>
  String(value || "analytics-report")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const PUMP_ENERGY_READING_KEYS = [
  "voltage_r", "voltage_y", "voltage_b",
  "current_r", "current_y", "current_b",
  "pf_r", "pf_y", "pf_b",
  "total_kw", "total_kw_max", "total_kwh", "frequency", "avg_vll",
];

// A tank can be fed by several pumps; older rows carry only associated_pump_id.
const formatAssociatedPumps = (row) => {
  const pumpIds = (
    Array.isArray(row?.associated_pump_ids) ? row.associated_pump_ids : []
  ).filter(Boolean);
  if (pumpIds.length) return pumpIds.join(", ");
  return row?.associated_pump_id || "";
};

const isEmptyValue = (value) =>
  value === null || value === undefined || value === "";

const isUsableLabelValue = (value) => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim();
  return Boolean(
    text && !["-", "null", "none", "undefined"].includes(text.toLowerCase()),
  );
};

const formatAssetDropdownLabel = (name, customId) => {
  const id = isUsableLabelValue(customId) ? String(customId).trim() : "";
  if (!isUsableLabelValue(name)) return id || "-";
  return id ? `${String(name).trim()} (${id})` : String(name).trim();
};

const formatExportValue = (value) => {
  if (isEmptyValue(value)) return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const formatNumber = (value, digits = 2) => {
  // Number(null) is 0, so a missing reading must be caught before converting.
  if (value === null || value === undefined || value === "") return "-";
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toFixed(digits);
};

const formatRawNumber = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return String(numericValue);
};

const formatFixedNumber = (value, digits = 3) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return numericValue.toFixed(digits);
};

const formatIstDownloadDateTime = () =>
  new Date().toLocaleString("en-IN", {
    timeZone: IST_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });

const formatHumanDate = (dateValue) => {
  if (!dateValue) return "-";
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(dateValue);
  const [, year, monthValue, day] = match;
  const date = new Date(Number(year), Number(monthValue) - 1, Number(day));
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getTodayIstDateString = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const values = parts.reduce((map, part) => {
    map[part.type] = part.value;
    return map;
  }, {});

  return `${values.year}-${values.month}-${values.day}`;
};

const getCurrentIstMonthString = () => getTodayIstDateString().slice(0, 7);

const validateDailyDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return "Please select both start and end dates";

  const todayIst = getTodayIstDateString();
  if (startDate > todayIst || endDate > todayIst) {
    return "Future dates are not allowed";
  }

  if (endDate < startDate) return "End date cannot be before start date";

  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  if (diffDays > 60) return "Date range cannot exceed 60 days";

  return null;
};

const formatHumanMonth = (monthValue) => {
  if (!monthValue) return "-";
  const match = String(monthValue).match(/^(\d{4})-(\d{2})$/);
  if (!match) return String(monthValue);
  const [, year, month] = match;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const formatHumanDateTime = (dateTimeValue) => {
  if (!dateTimeValue) return "-";

  const numericValue = Number(dateTimeValue);
  if (Number.isFinite(numericValue)) {
    const timestampMs =
      numericValue < 100000000000 ? numericValue * 1000 : numericValue;
    return new Date(timestampMs).toLocaleString("en-IN", {
      timeZone: IST_TIME_ZONE,
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  }

  const value = String(dateTimeValue);
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/,
  );
  if (!match) return value;
  const [, year, month, day, hour = "00", minute = "00", second = "00"] = match;

  if (/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleString("en-IN", {
        timeZone: IST_TIME_ZONE,
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    }
  }

  const hourNumber = Number(hour);
  const hour12 = hourNumber % 12 || 12;
  const period = hourNumber >= 12 ? "pm" : "am";
  const monthLabel = new Intl.DateTimeFormat("en-IN", {
    month: "short",
  }).format(new Date(Date.UTC(Number(year), Number(month) - 1, 1)));

  return `${day} ${monthLabel} ${year}, ${String(hour12).padStart(2, "0")}:${minute}:${second} ${period}`;
};

const formatDateRangeLabel = (filterType, start, end, month) => {
  if (filterType === "monthly") return formatHumanMonth(month);
  return `${formatHumanDate(start)} to ${formatHumanDate(end)}`;
};

const csvEscape = (value) => {
  const text = formatExportValue(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const downloadBlob = (content, fileName, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const downloadCsvReport = (config) => {
  const rows = [
    ["Report", config.title],
    ...config.metadata.map((item) => [item.label, item.value]),
    [],
    config.columns.map((column) => column.label),
    ...config.rows.map((row) =>
      config.columns.map((column) => column.getValue(row)),
    ),
  ];
  const csv = rows.map((row) => row.map(csvEscape).join(",")).join("\r\n");
  downloadBlob(
    `\uFEFF${csv}`,
    `${config.fileName}.csv`,
    "text/csv;charset=utf-8;",
  );
};

const pdfText = (value) =>
  formatExportValue(value)
    .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const escapePdfText = (value) =>
  pdfText(value)
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");

const estimateTextWidth = (text, fontSize) =>
  pdfText(text).length * fontSize * 0.5;

const wrapPdfText = (value, maxWidth, fontSize) => {
  const text = pdfText(value);
  if (!text) return ["-"];

  const words = text.split(" ");
  const lines = [];
  let line = "";

  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (estimateTextWidth(candidate, fontSize) <= maxWidth) {
      line = candidate;
      return;
    }

    if (line) {
      lines.push(line);
      line = "";
    }

    if (estimateTextWidth(word, fontSize) <= maxWidth) {
      line = word;
      return;
    }

    let chunk = "";
    word.split("").forEach((char) => {
      const chunkCandidate = `${chunk}${char}`;
      if (estimateTextWidth(chunkCandidate, fontSize) <= maxWidth) {
        chunk = chunkCandidate;
      } else {
        if (chunk) lines.push(chunk);
        chunk = char;
      }
    });
    line = chunk;
  });

  if (line) lines.push(line);
  return lines.length ? lines : ["-"];
};

const buildColumnWidths = (columns, rows, tableWidth) => {
  const weights = columns.map((column) => {
    const samples = rows.slice(0, 60).map((row) => column.getValue(row));
    const maxLength = Math.max(
      column.label.length,
      ...samples.map((value) => pdfText(value).length),
    );
    return Math.max(6, Math.min(22, maxLength));
  });
  const totalWeight = weights.reduce((sum, value) => sum + value, 0) || 1;
  return weights.map((weight) => (weight / totalWeight) * tableWidth);
};

const createPdfReport = (config) => {
  const { width, height, margin } = PDF_PAGE;
  const tableWidth = width - margin * 2;
  const titleSize = 14;
  const metaSize = 8;
  const tableSize = 6.2;
  const lineHeight = tableSize + 2.2;
  const columnWidths = buildColumnWidths(
    config.columns,
    config.rows,
    tableWidth,
  );
  const metadataGap = 8;
  const metadataColumns = 2;
  const metadataCardWidth =
    (tableWidth - metadataGap * (metadataColumns - 1)) / metadataColumns;
  const pages = [[]];
  let pageIndex = 0;
  let y = margin;

  const currentPage = () => pages[pageIndex];
  const push = (command) => currentPage().push(command);
  const addPage = () => {
    pages.push([]);
    pageIndex += 1;
    y = margin;
  };
  const ensureSpace = (space) => {
    if (y + space > height - margin) addPage();
  };
  const setFillColor = (color = [0, 0, 0]) => {
    push(`${color.map((value) => value.toFixed(3)).join(" ")} rg`);
  };
  const setStrokeColor = (color = [0, 0, 0]) => {
    push(`${color.map((value) => value.toFixed(3)).join(" ")} RG`);
  };
  const drawRect = (x, topY, rectWidth, rectHeight, color) => {
    setFillColor(color);
    push(
      `${x.toFixed(2)} ${(height - topY - rectHeight).toFixed(2)} ${rectWidth.toFixed(2)} ${rectHeight.toFixed(2)} re f`,
    );
  };
  const drawText = (
    text,
    x,
    topY,
    size = tableSize,
    font = "F1",
    color = [0.08, 0.11, 0.18],
  ) => {
    setFillColor(color);
    const baseline = height - topY - size;
    push(
      `BT /${font} ${size} Tf ${x.toFixed(2)} ${baseline.toFixed(2)} Td (${escapePdfText(text)}) Tj ET`,
    );
  };
  const drawLine = (x1, topY1, x2, topY2) => {
    setStrokeColor([0.82, 0.86, 0.91]);
    push(
      `${x1.toFixed(2)} ${(height - topY1).toFixed(2)} m ${x2.toFixed(2)} ${(height - topY2).toFixed(2)} l S`,
    );
  };
  const drawHorizontalLine = (topY) =>
    drawLine(margin, topY, width - margin, topY);
  const drawMetadataCards = (items) => {
    drawText("Report Information", margin, y, 9.5, "F2", [0.03, 0.36, 0.57]);
    y += 16;

    for (let index = 0; index < items.length; index += metadataColumns) {
      const rowItems = items.slice(index, index + metadataColumns);
      const rowHeights = rowItems.map((item) => {
        const labelLines = wrapPdfText(
          item.label,
          metadataCardWidth - 14,
          metaSize,
        );
        const valueLines = wrapPdfText(
          item.value,
          metadataCardWidth - 14,
          metaSize + 0.4,
        );
        return 16 + (labelLines.length + valueLines.length) * (metaSize + 2);
      });
      const rowHeight = Math.max(...rowHeights, 28);
      ensureSpace(rowHeight + 7);

      rowItems.forEach((item, itemIndex) => {
        const cardX = margin + itemIndex * (metadataCardWidth + metadataGap);
        const labelLines = wrapPdfText(
          item.label,
          metadataCardWidth - 14,
          metaSize,
        );
        const valueLines = wrapPdfText(
          item.value,
          metadataCardWidth - 14,
          metaSize + 0.4,
        );

        drawRect(cardX, y, metadataCardWidth, rowHeight, [0.94, 0.98, 1]);
        drawRect(cardX, y, 3, rowHeight, [0.05, 0.57, 0.82]);
        labelLines.forEach((line, lineIndex) => {
          drawText(
            line,
            cardX + 9,
            y + 7 + lineIndex * (metaSize + 2),
            metaSize,
            "F2",
            [0.23, 0.34, 0.46],
          );
        });
        valueLines.forEach((line, lineIndex) => {
          drawText(
            line,
            cardX + 9,
            y +
              8 +
              labelLines.length * (metaSize + 2) +
              lineIndex * (metaSize + 2),
            metaSize + 0.4,
            "F1",
            [0.02, 0.09, 0.18],
          );
        });
      });
      y += rowHeight + 7;
    }
  };
  const drawTableHeader = () => {
    const headerLines = config.columns.map((column, index) =>
      wrapPdfText(column.label, columnWidths[index] - 4, tableSize),
    );
    const headerHeight =
      Math.max(...headerLines.map((lines) => lines.length)) * lineHeight + 8;
    ensureSpace(headerHeight + lineHeight);
    drawRect(margin, y, tableWidth, headerHeight, [0.05, 0.57, 0.82]);
    let x = margin;
    headerLines.forEach((lines, index) => {
      lines.forEach((line, lineIndex) => {
        drawText(
          line,
          x + 3,
          y + 4 + lineIndex * lineHeight,
          tableSize,
          "F2",
          [1, 1, 1],
        );
      });
      x += columnWidths[index];
    });
    y += headerHeight;
    drawHorizontalLine(y);
  };

  drawRect(margin, y, tableWidth, 25, [0.91, 0.97, 1]);
  drawText(
    config.title,
    margin + 9,
    y + 7,
    titleSize,
    "F2",
    [0.03, 0.36, 0.57],
  );
  y += 34;
  drawMetadataCards(config.metadata);
  y += 7;
  drawHorizontalLine(y);
  y += 12;
  drawTableHeader();

  config.rows.forEach((row, rowIndex) => {
    const cellLines = config.columns.map((column, index) =>
      wrapPdfText(column.getValue(row), columnWidths[index] - 4, tableSize),
    );
    const rowHeight =
      Math.max(...cellLines.map((lines) => lines.length)) * lineHeight + 8;

    if (y + rowHeight > height - margin) {
      addPage();
      drawTableHeader();
    }

    drawRect(
      margin,
      y,
      tableWidth,
      rowHeight,
      rowIndex % 2 === 0 ? [1, 1, 1] : [0.97, 0.98, 1],
    );
    let x = margin;
    cellLines.forEach((lines, index) => {
      lines.forEach((line, lineIndex) => {
        drawText(line, x + 3, y + 4 + lineIndex * lineHeight, tableSize);
      });
      x += columnWidths[index];
    });
    y += rowHeight;
    drawHorizontalLine(y);
  });

  const objects = [];
  const addObject = (content) => {
    objects.push(content);
    return objects.length;
  };
  const catalogId = addObject("<< /Type /Catalog /Pages 2 0 R >>");
  const pagesId = addObject("");
  const fontId = addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  );
  const boldFontId = addObject(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  );
  const pageIds = [];

  pages.forEach((commands) => {
    const stream = commands.join("\n");
    const contentId = addObject(
      `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    );
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /Font << /F1 ${fontId} 0 R /F2 ${boldFontId} 0 R >> >> /Contents ${contentId} 0 R >>`,
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] =
    `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageIds.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
};

const downloadPdfReport = (config) => {
  const pdf = createPdfReport(config);
  downloadBlob(pdf, `${config.fileName}.pdf`, "application/pdf");
};

const TankPercentageGraph = () => {
  const { isSuperAdmin } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState("tank-level");

  // Data states
  const [societyInfo, setSocietyInfo] = useState(null);
  const [tanks, setTanks] = useState([]);
  const [pumps, setPumps] = useState([]);
  const [pumpsSocietyId, setPumpsSocietyId] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [pumpGraphData, setPumpGraphData] = useState([]);

  // Selection states
  const [selectedSociety, setSelectedSociety] = useState("");
  const [selectedTank, setSelectedTank] = useState("");
  const [selectedPump, setSelectedPump] = useState("");
  const [pumpMetric, setPumpMetric] = useState("voltage"); // voltage, current, pf
  const [pumpPhase, setPumpPhase] = useState("r"); // r, y, b

  // Valve states
  const [valves, setValves] = useState([]);
  const [valveLogData, setValveLogData] = useState([]);
  const [selectedValve, setSelectedValve] = useState("");
  const [loadingValves, setLoadingValves] = useState(false);
  const [valveLoadingLogs, setValveLoadingLogs] = useState(false);
  const [valveInfo, setValveInfo] = useState(null);
  const [valveCurrentPage, setValveCurrentPage] = useState(1);
  const valvePageSize = 20;

  // Pump Hours states
  const [pumpHoursLoadingLogs, setPumpHoursLoadingLogs] = useState(false);
  const [pumpHoursInfo, setPumpHoursInfo] = useState(null);

  // Pump Logs states
  const [pumpLogData, setPumpLogData] = useState([]);
  const [pumpLogsLoading, setPumpLogsLoading] = useState(false);
  const [pumpLogsInfo, setPumpLogsInfo] = useState(null);
  const [pumpLogsCurrentPage, setPumpLogsCurrentPage] = useState(1);
  const pumpLogsPageSize = 20;

  // Valve Duration states
  const [valveDurationData, setValveDurationData] = useState(null);
  const [valveDurationLoading, setValveDurationLoading] = useState(false);
  const [valveDurationInfo, setValveDurationInfo] = useState(null);

  // Water Flow states
  const [waterFlowData, setWaterFlowData] = useState([]);
  const [waterFlowInfo, setWaterFlowInfo] = useState(null);
  const [waterFlowLoading, setWaterFlowLoading] = useState(false);
  const [waterFlowView, setWaterFlowView] = useState("daily");
  const [waterFlowDate, setWaterFlowDate] = useState("");
  const [waterFlowMonth, setWaterFlowMonth] = useState("");

  // Filter states
  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [month, setMonth] = useState("");

  // Loading states
  const [loadingTanks, setLoadingTanks] = useState(false);
  const [loadingPumps, setLoadingPumps] = useState(false);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [pumpLoadingGraph, setPumpLoadingGraph] = useState(false);
  const [tankInfo, setTankInfo] = useState(null);
  const [pumpInfo, setPumpInfo] = useState(null);

  // Fetch societies on mount (Super-admin only)
  useEffect(() => {
    if (isSuperAdmin()) {
      fetchSocieties();
    }
  }, [isSuperAdmin]);

  const fetchSocieties = async () => {
    try {
      const response = await api.getSocieties();
      const society = Array.isArray(response.data)
        ? response.data[0]
        : response.data;

      if (society?.id) {
        setSocietyInfo(society);
        setSelectedSociety(society.id);
      }
    } catch (error) {
      console.error("Failed to fetch societies:", error);
      toast.error(getErrorMessage(error, "Failed to load societies"));
    }
  };

  // Fetch society assets for analytics dropdowns.
  useEffect(() => {
    if (selectedSociety || !isSuperAdmin()) {
      fetchSocietyTanks(selectedSociety);
      fetchSocietyValves(selectedSociety);
    }
  }, [selectedSociety, isSuperAdmin]);

  useEffect(() => {
    const pumpTabs = ["pump-energy", "pump-hours", "pump-logs"];

    if (
      pumpTabs.includes(activeTab) &&
      !loadingPumps &&
      pumpsSocietyId !== (selectedSociety || "")
    ) {
      fetchSocietyPumps(selectedSociety);
    }
  }, [activeTab, selectedSociety, pumpsSocietyId, loadingPumps]);

  const fetchSocietyTanks = async (societyId = selectedSociety) => {
    setLoadingTanks(true);
    try {
      const response = await api.getSocietyStorageAssets(societyId || null);
      setTanks(response.storage_assets || response.tanks || []);
      setSelectedTank("");
      setGraphData([]);
      setWaterFlowData([]);
      setWaterFlowInfo(null);
    } catch (error) {
      console.error("Failed to fetch tanks:", error);
      toast.error(
        getErrorMessage(error, "Failed to load tanks for this society"),
      );
      setTanks([]);
    } finally {
      setLoadingTanks(false);
    }
  };

  const fetchSocietyValves = async (societyId = selectedSociety) => {
    setLoadingValves(true);
    try {
      const response = await api.getSocietyValves(societyId || null);
      setValves(response.valves || []);
      setSelectedValve("");
      setValveLogData([]);
      setValveInfo(null);
    } catch (error) {
      console.error("Failed to fetch valves:", error);
      toast.error(
        getErrorMessage(error, "Failed to load valves for this society"),
      );
      setValves([]);
    } finally {
      setLoadingValves(false);
    }
  };

  // Fetch all pumps available for the current society.
  const fetchSocietyPumps = async (societyId = selectedSociety) => {
    setLoadingPumps(true);
    try {
      const response = await api.getSocietyPumps(societyId || null);
      setPumps(response.pumps || []);
      setPumpsSocietyId(societyId || "");
      setSelectedPump("");
      setPumpGraphData([]);
      setPumpInfo(null);
      setPumpHoursInfo(null);
      setPumpLogData([]);
      setPumpLogsInfo(null);
    } catch (error) {
      console.error("Failed to fetch pumps:", error);
      toast.error(
        getErrorMessage(error, "Failed to load pumps for this society"),
      );
      setPumps([]);
      setPumpsSocietyId(null);
    } finally {
      setLoadingPumps(false);
    }
  };

  // Initialize date/month filters when storage asset is selected
  useEffect(() => {
    if (selectedTank) {
      const today = new Date();
      const yesterdayDate = new Date(today);
      yesterdayDate.setDate(yesterdayDate.getDate() - 1);

      const todayString = today.toISOString().split("T")[0];
      const yesterdayString = yesterdayDate.toISOString().split("T")[0];
      const monthString = today.toISOString().slice(0, 7);

      setStartDate(yesterdayString);
      setEndDate(todayString);
      setMonth(monthString);
      setWaterFlowDate(todayString);
      setWaterFlowMonth(monthString);
      setWaterFlowData([]);
      setWaterFlowInfo(null);

      if (activeTab === "tank-level") {
        fetchTankData(
          selectedTank,
          null,
          "daily",
          yesterdayString,
          todayString,
          null,
        );
      }
    }
  }, [selectedTank]);

  useEffect(() => {
    if (
      activeTab === "tank-level" &&
      selectedTank &&
      startDate &&
      endDate &&
      graphData.length === 0 &&
      !loadingGraph
    ) {
      fetchTankData(selectedTank, null, "daily", startDate, endDate, null);
    }
  }, [activeTab]);

  const fetchTankData = async (tankId, towerId, type, start, end, monthVal) => {
    setLoadingGraph(true);
    try {
      const response = await api.getStorageAssetData(
        tankId,
        towerId,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );

      // Transform data based on response format
      let transformedData = [];
      if (response.data && Array.isArray(response.data)) {
        transformedData = response.data.map((item) => ({
          ...item,
          time: item.datetime ? formatDateTime(item.datetime, type) : item.time,
        }));
      }

      setGraphData(transformedData);
      setTankInfo({
        tank_id: response.asset_id || response.tank_id || tankId,
        asset_key: response.asset_key || tankId,
        asset_id: response.asset_id || response.tank_id || tankId,
        asset_type_label: response.asset_type_label,
        name: response.asset_name || response.tank_name,
        capacity: response.capacity,
        dataPoints: response.count,
        statistics: response.statistics,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
        month: response.month || monthVal,
        monthRange: response.month_range,
      });

      const typeLabel = type === "daily" ? "daily" : "monthly";
      toast.success(`Loaded ${response.count} ${typeLabel} data points`);
    } catch (error) {
      console.error("Failed to fetch graph data:", error);
      toast.error(getErrorMessage(error, "Failed to load storage asset data"));
      setGraphData([]);
      setTankInfo(null);
    } finally {
      setLoadingGraph(false);
    }
  };

  // Fetch pump energy data
  const fetchPumpEnergyData = async (pumpId, type, start, end, monthVal) => {
    setPumpLoadingGraph(true);
    try {
      const response = await api.getPumpEnergy(
        pumpId,
        null,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );

      // Transform data for charting
      let transformedData = [];
      if (response.data && Array.isArray(response.data)) {
        transformedData = response.data.map((item) => ({
          ...item,
          time: item.datetime ? formatHumanDateTime(item.datetime) : item.time,
        }));
      }

      // A disconnected energy meter sends "-" for every reading, which the API
      // returns as null. If no point has any reading, show "no data" rather
      // than plotting an empty (or zero) line.
      const hasEnergyReading = transformedData.some((item) =>
        PUMP_ENERGY_READING_KEYS.some((key) =>
          Number.isFinite(Number(item[key])) && item[key] !== null,
        ),
      );
      if (!hasEnergyReading) transformedData = [];

      setPumpGraphData(transformedData);
      setPumpInfo({
        pump_id: response.pump_id,
        tower_id: response.tower_id,
        energyMeterConnected: response.energy_meter_connected,
        dataPoints: transformedData.length,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
        month: response.month || monthVal,
        monthRange: response.month_range,
      });

      const typeLabel = type === "daily" ? "daily" : "monthly";
      if (hasEnergyReading) {
        toast.success(
          `Loaded ${transformedData.length} ${typeLabel} pump energy data points`,
        );
      } else {
        toast.info(
          response.energy_meter_connected === false
            ? "Energy meter is not connected for this pump"
            : "No energy meter readings in this period",
        );
      }
    } catch (error) {
      console.error("Failed to fetch pump data:", error);
      toast.error(getErrorMessage(error, "Failed to load pump energy data"));
      setPumpGraphData([]);
      setPumpInfo(null);
    } finally {
      setPumpLoadingGraph(false);
    }
  };

  const formatDateTime = (datetime, type) => {
    try {
      return formatHumanDateTime(datetime);
    } catch (e) {
      return datetime;
    }
  };

  const formatWaterVolume = (liters) => {
    const numericValue = Number(liters);
    if (!Number.isFinite(numericValue)) return "-";

    if (Math.abs(numericValue) >= 1000) {
      return `${formatFixedNumber(numericValue / 1000, 3)} KL`;
    }

    return `${formatFixedNumber(numericValue, 3)} L`;
  };

  const getErrorMessage = (error, fallback) => {
    return getApiErrorMessage(error, fallback);
  };

  const fetchWaterFlowData = async (
    tankId,
    towerId,
    view,
    dateVal,
    monthVal,
  ) => {
    setWaterFlowLoading(true);
    try {
      const response = await api.getStorageAssetWaterFlow(
        tankId,
        towerId,
        view,
        dateVal,
        monthVal,
        selectedSociety || null,
      );
      const chartData = (response.data || []).map((item) => ({
        ...item,
        inflow_kl: Number(item.inflow_l || 0) / 1000,
        outflow_kl: Number(item.outflow_l || 0) / 1000,
        outflow_negative_kl: -(Number(item.outflow_l || 0) / 1000),
      }));

      setWaterFlowData(chartData);
      setWaterFlowInfo({
        tankName: response.asset_name || response.tank_name,
        tankId: response.asset_id || response.tank_id,
        assetKey: response.asset_key || tankId,
        assetType: response.asset_type_label,
        towerId: response.tower_id || towerId,
        capacity: response.tank_capacity_l,
        view: response.view || view,
        dateRange: response.date_range,
        summary: response.summary,
        threshold: response.threshold_l,
      });
      toast.success("Water flow data loaded");
    } catch (error) {
      console.error("Failed to fetch water flow data:", error);
      toast.error(getErrorMessage(error, "Failed to load water flow data"));
      setWaterFlowData([]);
      setWaterFlowInfo(null);
    } finally {
      setWaterFlowLoading(false);
    }
  };

  const handleApplyWaterFlowFilter = () => {
    if (!selectedTank) {
      toast.error("Please select a storage asset");
      return;
    }

    if (waterFlowView === "daily") {
      if (!waterFlowDate) {
        toast.error("Please select a date");
        return;
      }
      if (waterFlowDate > getTodayIstDateString()) {
        toast.error("Future dates are not allowed");
        return;
      }
      fetchWaterFlowData(selectedTank, null, "daily", waterFlowDate, null);
      return;
    }

    if (!waterFlowMonth) {
      toast.error("Please select a month");
      return;
    }
    if (waterFlowMonth > getTodayIstDateString().slice(0, 7)) {
      toast.error("Future months are not allowed");
      return;
    }
    fetchWaterFlowData(selectedTank, null, waterFlowView, null, waterFlowMonth);
  };

  const handleFilterTypeChange = (type) => {
    setFilterType(type);
    if (type === "monthly" && !month) {
      setMonth(getCurrentIstMonthString());
    }
  };

  const handleApplyFilter = () => {
    if (selectedTank) {
      if (filterType === "daily") {
        if (!startDate || !endDate) {
          toast.error("Please select both start and end dates");
          return;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        if (diffDays > 60) {
          toast.error("Date range cannot exceed 60 days");
          return;
        }
        fetchTankData(selectedTank, null, "daily", startDate, endDate, null);
      } else {
        if (!month) {
          toast.error("Please select a month");
          return;
        }
        fetchTankData(selectedTank, null, "monthly", null, null, month);
      }
    }
  };

  // Fetch valve log data
  const fetchValveLogData = async (
    valveId,
    towerId,
    type,
    start,
    end,
    monthVal,
  ) => {
    setValveLoadingLogs(true);
    try {
      const response = await api.getValveLogs(
        valveId,
        towerId,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );
      setValveLogData(response.data || []);
      setValveInfo({
        valve_id: response.valve_id,
        tower_id: response.tower_id || towerId,
        device_id: response.device_id,
        stateChanges: response.count,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
      });
      setValveCurrentPage(1);
      toast.success(`Found ${response.count} state changes`);
    } catch (error) {
      console.error("Failed to fetch valve logs:", error);
      toast.error(getErrorMessage(error, "Failed to load valve log data"));
      setValveLogData([]);
      setValveInfo(null);
    } finally {
      setValveLoadingLogs(false);
    }
  };

  const handleApplyValveFilter = () => {
    if (selectedValve) {
      if (filterType === "daily") {
        if (!startDate || !endDate) {
          toast.error("Please select both start and end dates");
          return;
        }
        const start = new Date(startDate);
        const end = new Date(endDate);
        const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        if (diffDays > 60) {
          toast.error("Date range cannot exceed 60 days");
          return;
        }
        fetchValveLogData(
          selectedValve,
          null,
          "daily",
          startDate,
          endDate,
          null,
        );
      } else {
        if (!month) {
          toast.error("Please select a month");
          return;
        }
        fetchValveLogData(selectedValve, null, "monthly", null, null, month);
      }
    }
  };

  // Fetch pump running hours data
  const fetchPumpRunningHours = async (pumpId, type, start, end, monthVal) => {
    setPumpHoursLoadingLogs(true);
    try {
      const response = await api.getPumpRunningHours(
        pumpId,
        null,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );
      setPumpHoursInfo({
        pump_id: response.pump_id,
        tower_id: response.tower_id,
        device_id: response.device_id,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
        sessions: response.sessions || [],
        sessionCount: response.session_count,
        totalRunning: response.total_running_formatted,
        totalSeconds: response.total_running_seconds,
        dailyBreakdown: response.daily_breakdown || [],
      });
      toast.success(
        `Found ${response.session_count} running sessions (Total: ${response.total_running_formatted})`,
      );
    } catch (error) {
      console.error("Failed to fetch pump running hours:", error);
      toast.error(getErrorMessage(error, "Failed to load pump running hours"));
      setPumpHoursInfo(null);
    } finally {
      setPumpHoursLoadingLogs(false);
    }
  };

  const handleApplyPumpHoursFilter = () => {
    if (!selectedPump) {
      toast.error("Please select a pump");
      return;
    }

    if (filterType === "daily") {
      const validationMessage = validateDailyDateRange(startDate, endDate);
      if (validationMessage) {
        toast.error(validationMessage);
        return;
      }
      fetchPumpRunningHours(selectedPump, "daily", startDate, endDate, null);
    } else {
      if (!month) {
        toast.error("Please select a month");
        return;
      }
      if (month > getTodayIstDateString().slice(0, 7)) {
        toast.error("Future months are not allowed");
        return;
      }
      fetchPumpRunningHours(selectedPump, "monthly", null, null, month);
    }
  };

  // Fetch pump state change logs
  const fetchPumpLogData = async (pumpId, type, start, end, monthVal) => {
    setPumpLogsLoading(true);
    try {
      const response = await api.getPumpLogs(
        pumpId,
        null,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );
      setPumpLogData(response.data || []);
      setPumpLogsInfo({
        pump_id: response.pump_id,
        tower_id: response.tower_id,
        device_id: response.device_id,
        stateChanges: response.count,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
      });
      setPumpLogsCurrentPage(1);
      toast.success(`Found ${response.count} pump state changes`);
    } catch (error) {
      console.error("Failed to fetch pump logs:", error);
      toast.error(getErrorMessage(error, "Failed to load pump log data"));
      setPumpLogData([]);
      setPumpLogsInfo(null);
    } finally {
      setPumpLogsLoading(false);
    }
  };

  const handleApplyPumpLogsFilter = () => {
    if (!selectedPump) {
      toast.error("Please select a pump");
      return;
    }

    if (filterType === "daily") {
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return;
      }
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (diffDays > 60) {
        toast.error("Date range cannot exceed 60 days");
        return;
      }
      fetchPumpLogData(selectedPump, "daily", startDate, endDate, null);
    } else {
      if (!month) {
        toast.error("Please select a month");
        return;
      }
      fetchPumpLogData(selectedPump, "monthly", null, null, month);
    }
  };

  const fetchValveDuration = async (
    valveId,
    towerId,
    type,
    start,
    end,
    monthVal,
  ) => {
    setValveDurationLoading(true);
    try {
      const response = await api.getValveDuration(
        valveId,
        towerId,
        type,
        start,
        end,
        monthVal,
        selectedSociety || null,
      );
      setValveDurationData(response);
      setValveDurationInfo({
        valve_id: response.valve_id,
        tower_id: response.tower_id || towerId,
        device_id: response.device_id,
        filterType: response.filter_type || type,
        dateRange: response.date_range,
        session_count: response.session_count,
        total_open: response.total_open_formatted,
        total_open_seconds: response.total_open_seconds,
      });
      toast.success(`Found ${response.session_count} OPEN sessions`);
    } catch (error) {
      console.error("Failed to fetch valve duration:", error);
      toast.error(getErrorMessage(error, "Failed to load valve duration data"));
      setValveDurationData(null);
      setValveDurationInfo(null);
    } finally {
      setValveDurationLoading(false);
    }
  };

  const handleApplyValveDurationFilter = () => {
    if (selectedValve) {
      if (filterType === "daily") {
        const validationMessage = validateDailyDateRange(startDate, endDate);
        if (validationMessage) {
          toast.error(validationMessage);
          return;
        }
        fetchValveDuration(
          selectedValve,
          null,
          "daily",
          startDate,
          endDate,
          null,
        );
      } else {
        if (!month) {
          toast.error("Please select a month");
          return;
        }
        if (month > getTodayIstDateString().slice(0, 7)) {
          toast.error("Future months are not allowed");
          return;
        }
        fetchValveDuration(selectedValve, null, "monthly", null, null, month);
      }
    }
  };

  const handleApplyPumpFilter = () => {
    if (!selectedPump) {
      toast.error("Please select a pump");
      return;
    }

    if (filterType === "daily") {
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return;
      }
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      if (diffDays > 60) {
        toast.error("Date range cannot exceed 60 days");
        return;
      }
      fetchPumpEnergyData(selectedPump, "daily", startDate, endDate, null);
    } else {
      const selectedMonth = month || getCurrentIstMonthString();
      if (!month) {
        setMonth(selectedMonth);
      }
      fetchPumpEnergyData(selectedPump, "monthly", null, null, selectedMonth);
    }
  };

  const getTowerName = (towerId) =>
    towerId === NO_TOWER_ANALYTICS_ID ? "No Tower" : towerId || "-";

  const getSelectedStorageAsset = (assetKey = selectedTank) =>
    tanks.find(
      (asset) =>
        asset.asset_key === assetKey ||
        asset.tank_id === assetKey ||
        asset.asset_id === assetKey,
    );

  const getTankName = (tankId = selectedTank) =>
    tankInfo?.name ||
    waterFlowInfo?.tankName ||
    getSelectedStorageAsset(tankId)?.name ||
    getSelectedStorageAsset(tankId)?.asset_name ||
    tankId ||
    "-";

  const getSocietyName = () =>
    societyInfo?.name ||
    societyInfo?.society_name ||
    selectedSociety ||
    "Current Society";

  const getSelectedDateRangeLabel = (info = {}) => {
    if (info.dateRange?.date) return formatHumanDate(info.dateRange.date);
    if (info.dateRange?.month) return formatHumanMonth(info.dateRange.month);
    if (info.dateRange?.start && info.dateRange?.end) {
      return `${formatHumanDate(info.dateRange.start)} to ${formatHumanDate(info.dateRange.end)}`;
    }
    if (info.monthRange?.start && info.monthRange?.end) {
      return `${formatHumanDate(info.monthRange.start)} to ${formatHumanDate(info.monthRange.end)}`;
    }
    if (info.month) return formatHumanMonth(info.month);
    return formatDateRangeLabel(filterType, startDate, endDate, month);
  };

  const createExportConfig = ({
    title,
    rows,
    columns,
    metadata = [],
    info = {},
    fileSlug,
  }) => {
    const resolvedFilterType = info.view || info.filterType || filterType;
    const generatedAt = formatIstDownloadDateTime();

    return {
      title,
      rows: rows || [],
      columns,
      fileName: `${sanitizeFileName(fileSlug || title)}-${sanitizeFileName(generatedAt)}`,
      metadata: [
        { label: "Society Name", value: getSocietyName() },
        {
          label: "Tower ID",
          value: info.tower_id || info.towerId || "-",
        },
        {
          label: "Tower Name",
          value: getTowerName(info.tower_id || info.towerId),
        },
        { label: "Download Datetime (IST)", value: generatedAt },
        { label: "Filter Type", value: resolvedFilterType },
        {
          label: "Selected Date Range Filter",
          value: getSelectedDateRangeLabel(info),
        },
        ...metadata,
        { label: "Row Count", value: rows?.length || 0 },
      ],
    };
  };

  const tankLevelExportConfig = () => {
    const isMonthly =
      tankInfo?.filterType === "monthly" || filterType === "monthly";
    return createExportConfig({
      title: "Tank Level Analytics",
      fileSlug: "storage-asset-level",
      rows: graphData,
      info: tankInfo,
      metadata: [
        {
          label: "Storage Asset ID",
          value: tankInfo?.asset_id || tankInfo?.tank_id || selectedTank,
        },
        {
          label: "Storage Asset Name",
          value: getTankName(tankInfo?.tank_id || selectedTank),
        },
        {
          label: "Storage Asset Type",
          value:
            tankInfo?.asset_type_label ||
            getSelectedStorageAsset()?.asset_type_label ||
            "-",
        },
        {
          label: "Storage Asset Capacity",
          value: tankInfo?.capacity ? `${tankInfo.capacity} L` : "-",
        },
        {
          label: "Average Percentage",
          value: `${tankInfo?.statistics?.average_percentage ?? "-"}%`,
        },
        {
          label: "Minimum Percentage",
          value: `${tankInfo?.statistics?.min_percentage ?? "-"}%`,
        },
        {
          label: "Maximum Percentage",
          value: `${tankInfo?.statistics?.max_percentage ?? "-"}%`,
        },
      ],
      columns: isMonthly
        ? [
            {
              label: "Date Time (IST)",
              getValue: (row) => formatHumanDateTime(row.datetime || row.time),
            },
            {
              label: "Average Percentage (%)",
              getValue: (row) => formatNumber(row.percentage, 2),
            },
            {
              label: "Minimum Percentage (%)",
              getValue: (row) => formatNumber(row.min_percentage, 2),
            },
            {
              label: "Maximum Percentage (%)",
              getValue: (row) => formatNumber(row.max_percentage, 2),
            },
          ]
        : [
            {
              label: "Date Time (IST)",
              getValue: (row) => formatHumanDateTime(row.datetime || row.time),
            },
            {
              label: "Percentage (%)",
              getValue: (row) => formatNumber(row.percentage, 2),
            },
          ],
    });
  };

  const pumpEnergyExportConfig = () =>
    createExportConfig({
      title: "Pump Energy Analytics",
      fileSlug: "pump-energy",
      rows: pumpGraphData,
      info: pumpInfo,
      metadata: [
        { label: "Pump ID", value: pumpInfo?.pump_id || selectedPump },
        { label: "Selected Metric", value: pumpMetric },
        { label: "Selected Phase", value: pumpPhase.toUpperCase() },
      ],
      columns: [
        {
          label: "Date Time (IST)",
          getValue: (row) => formatHumanDateTime(row.datetime || row.time),
        },
        { label: "Pump State", getValue: (row) => row.pump_state },
        {
          label: "Voltage R (V)",
          getValue: (row) => formatNumber(row.voltage_r, 2),
        },
        {
          label: "Voltage Y (V)",
          getValue: (row) => formatNumber(row.voltage_y, 2),
        },
        {
          label: "Voltage B (V)",
          getValue: (row) => formatNumber(row.voltage_b, 2),
        },
        {
          label: "Average VLL",
          getValue: (row) => formatNumber(row.avg_vll, 2),
        },
        {
          label: "Current R (A)",
          getValue: (row) => formatNumber(row.current_r, 2),
        },
        {
          label: "Current Y (A)",
          getValue: (row) => formatNumber(row.current_y, 2),
        },
        {
          label: "Current B (A)",
          getValue: (row) => formatNumber(row.current_b, 2),
        },
        { label: "PF R", getValue: (row) => formatNumber(row.pf_r, 3) },
        { label: "PF Y", getValue: (row) => formatNumber(row.pf_y, 3) },
        { label: "PF B", getValue: (row) => formatNumber(row.pf_b, 3) },
        {
          label: "Frequency (Hz)",
          getValue: (row) => formatNumber(row.frequency, 2),
        },
        {
          label: "Total kW",
          getValue: (row) => formatNumber(row.total_kw ?? row.total_kw_max, 2),
        },
        {
          label: "Total kWh",
          getValue: (row) => formatNumber(row.total_kwh, 2),
        },
        { label: "Sample Count", getValue: (row) => row.sample_count },
      ],
    });

  const valveLogsExportConfig = () =>
    createExportConfig({
      title: "Valve State Change Logs",
      fileSlug: "valve-logs",
      rows: valveLogData,
      info: valveInfo,
      metadata: [
        { label: "Valve ID", value: valveInfo?.valve_id || selectedValve },
        { label: "Device ID", value: valveInfo?.device_id },
        { label: "State Changes", value: valveInfo?.stateChanges },
      ],
      columns: [
        {
          label: "Date Time (IST)",
          getValue: (row) => formatHumanDateTime(row.datetime),
        },
        { label: "Valve State", getValue: (row) => row.valve_state },
        { label: "Valve ID", getValue: (row) => row.valve_id },
        { label: "Tank ID", getValue: (row) => row.tank_id },
        {
          label: "Tank Percentage (%)",
          getValue: (row) => formatNumber(row.tank_percentage, 2),
        },
        {
          label: "Associated Pump IDs",
          getValue: (row) => formatAssociatedPumps(row),
        },
      ],
    });

  const pumpHoursExportConfig = () =>
    createExportConfig({
      title: "Pump Running Hours",
      fileSlug: "pump-running-hours",
      rows: pumpHoursInfo?.sessions || [],
      info: pumpHoursInfo,
      metadata: [
        { label: "Pump ID", value: pumpHoursInfo?.pump_id || selectedPump },
        { label: "Device ID", value: pumpHoursInfo?.device_id },
        { label: "Total Running Time", value: pumpHoursInfo?.totalRunning },
        { label: "Total Running Seconds", value: pumpHoursInfo?.totalSeconds },
        { label: "Session Count", value: pumpHoursInfo?.sessionCount },
      ],
      columns: [
        {
          label: "Start Time (IST)",
          getValue: (row) => formatHumanDateTime(row.start_datetime),
        },
        {
          label: "End Time (IST)",
          getValue: (row) => formatHumanDateTime(row.end_datetime),
        },
        { label: "Duration", getValue: (row) => row.duration_formatted },
        { label: "Duration Seconds", getValue: (row) => row.duration_seconds },
        { label: "Ongoing", getValue: (row) => Boolean(row.ongoing) },
      ],
    });

  const pumpLogsExportConfig = () =>
    createExportConfig({
      title: "Pump State Change Logs",
      fileSlug: "pump-logs",
      rows: pumpLogData,
      info: pumpLogsInfo,
      metadata: [
        { label: "Pump ID", value: pumpLogsInfo?.pump_id || selectedPump },
        { label: "Device ID", value: pumpLogsInfo?.device_id },
        { label: "State Changes", value: pumpLogsInfo?.stateChanges },
      ],
      columns: [
        {
          label: "Date Time (IST)",
          getValue: (row) => formatHumanDateTime(row.datetime),
        },
        { label: "Pump State", getValue: (row) => row.pump_state },
        { label: "Pump ID", getValue: (row) => row.pump_id },
        { label: "Pump Health", getValue: (row) => row.pump_health },
        // { label: "UG Tank ID", getValue: (row) => row.ugt_id },
        // {
        //   label: "UG Tank Level (%)",
        //   getValue: (row) => formatNumber(row.ugt_level, 2),
        // },
      ],
    });

  const valveDurationExportConfig = () =>
    createExportConfig({
      title: "Valve Open Duration",
      fileSlug: "valve-duration",
      rows: valveDurationData?.sessions || [],
      info: valveDurationInfo,
      metadata: [
        {
          label: "Valve ID",
          value: valveDurationInfo?.valve_id || selectedValve,
        },
        { label: "Device ID", value: valveDurationInfo?.device_id },
        { label: "Total Open Time", value: valveDurationInfo?.total_open },
        {
          label: "Total Open Seconds",
          value: valveDurationInfo?.total_open_seconds,
        },
        { label: "Session Count", value: valveDurationInfo?.session_count },
      ],
      columns: [
        {
          label: "Start Time (IST)",
          getValue: (row) => formatHumanDateTime(row.start_datetime),
        },
        {
          label: "End Time (IST)",
          getValue: (row) => formatHumanDateTime(row.end_datetime),
        },
        { label: "Duration", getValue: (row) => row.duration_formatted },
        { label: "Duration Seconds", getValue: (row) => row.duration_seconds },
        { label: "Ongoing", getValue: (row) => Boolean(row.ongoing) },
      ],
    });

  const waterFlowExportConfig = () =>
    createExportConfig({
      title: "Water Flow Analytics",
      fileSlug: "water-flow",
      rows: waterFlowData,
      info: waterFlowInfo,
      metadata: [
        {
          label: "Storage Asset ID",
          value: waterFlowInfo?.tankId || selectedTank,
        },
        {
          label: "Storage Asset Name",
          value: getTankName(waterFlowInfo?.tankId || selectedTank),
        },
        {
          label: "Storage Asset Type",
          value:
            waterFlowInfo?.assetType ||
            getSelectedStorageAsset()?.asset_type_label ||
            "-",
        },
        {
          label: "Storage Asset Capacity",
          value: waterFlowInfo?.capacity ? `${waterFlowInfo.capacity} L` : "-",
        },
        {
          label: "Threshold",
          value: waterFlowInfo?.threshold
            ? `${formatFixedNumber(waterFlowInfo.threshold, 3)} L`
            : "-",
        },
        {
          label: "Total Inflow",
          value: formatWaterVolume(waterFlowInfo?.summary?.total_inflow_l),
        },
        {
          label: "Total Outflow",
          value: formatWaterVolume(waterFlowInfo?.summary?.total_outflow_l),
        },
        {
          label: "Net Change",
          value: formatWaterVolume(waterFlowInfo?.summary?.net_change_l),
        },
        { label: "Packet Count", value: waterFlowInfo?.summary?.packet_count },
        {
          label: "Ignored Delta Count",
          value: waterFlowInfo?.summary?.ignored_delta_count,
        },
      ],
      columns: [
        {
          label: "Date Time (IST)",
          getValue: (row) => formatHumanDateTime(row.datetime),
        },
        { label: "Timestamp", getValue: (row) => row.timestamp },
        { label: "Label", getValue: (row) => row.label },
        { label: "Period Key", getValue: (row) => row.key },
        {
          label: "Inflow (L)",
          getValue: (row) => formatFixedNumber(row.inflow_l, 3),
        },
        {
          label: "Outflow (L)",
          getValue: (row) => formatFixedNumber(row.outflow_l, 3),
        },
        {
          label: "Net Change (L)",
          getValue: (row) => formatFixedNumber(row.net_l, 3),
        },
        { label: "Flow Count", getValue: (row) => row.flow_count },
        { label: "Packet Count", getValue: (row) => row.packet_count },
        { label: "Low Data", getValue: (row) => Boolean(row.low_data) },
        { label: "Average Days", getValue: (row) => row.average_days },
      ],
    });

  const handleExport = (config, format) => {
    if (!config?.rows?.length) {
      toast.error("No analytics data is available to export");
      return;
    }

    try {
      if (format === "csv") {
        downloadCsvReport(config);
      } else {
        downloadPdfReport(config);
      }
      toast.success(`${format.toUpperCase()} export started`);
    } catch (error) {
      console.error("Failed to export analytics data:", error);
      toast.error(getErrorMessage(error, "Failed to export analytics data"));
    }
  };

  const ExportActions = ({ config }) => {
    const disabled = !config?.rows?.length;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => handleExport(config, "csv")}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => handleExport(config, "pdf")}
          className="gap-2"
        >
          <FileText className="h-4 w-4" />
          PDF
        </Button>
      </div>
    );
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const isMonthly = filterType === "monthly";
      return (
        <div className="bg-white p-4 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-semibold text-slate-900 text-sm mb-2">
            📅 {data.time}
          </p>
          <div className="space-y-1">
            <p className="text-blue-600 font-semibold">
              {"Level: "}
              <span className="font-bold text-lg">{data.percentage}%</span>
            </p>
            {isMonthly && (
              <p className="text-slate-600 text-xs">
                🔻 Min: {data.min_percentage}% | 🔺 Max: {data.max_percentage}%
              </p>
            )}
            {!isMonthly && data.min_percentage && data.max_percentage && (
              <p className="text-slate-600 text-xs">
                🔻 Min: {data.min_percentage}% | 🔺 Max: {data.max_percentage}%
              </p>
            )}
            <p className="text-slate-500 text-xs pt-1 border-t border-slate-200 mt-1">
              Timezone: IST (UTC+05:30)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  const PumpCustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const metricLabels = {
        voltage: "Voltage (V)",
        current: "Current (A)",
        pf: "Power Factor",
      };
      const phaseLabels = { r: "Red (R)", y: "Yellow (Y)", b: "Blue (B)" };

      return (
        <div className="bg-white p-4 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-semibold text-slate-900 text-sm mb-2">
            📅 {data.time}
          </p>
          <div className="space-y-1">
            <p className="text-yellow-600 font-semibold">
              {metricLabels[pumpMetric]} - {phaseLabels[pumpPhase]}
            </p>
            {(() => {
              const reading = data[`${pumpMetric}_${pumpPhase}`];
              const unit =
                pumpMetric === "voltage" ? "V" : pumpMetric === "current" ? "A" : "";
              return (
                <p className="font-bold text-lg">
                  {reading === null || reading === undefined
                    ? "No data"
                    : `${reading}${unit}`}
                </p>
              );
            })()}
            {data.pump_state && (
              <p className="text-slate-600 text-xs mt-2">
                Pump: {data.pump_state === "ON" ? "🟢 Running" : "🔴 Stopped"}
              </p>
            )}
            <p className="text-slate-500 text-xs pt-1 border-t border-slate-200 mt-1">
              Timezone: IST (UTC+05:30)
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900">
              Advanced Tank Analytics
            </h1>
            <p className="text-slate-600 mt-1">
              Comprehensive monitoring dashboard for water management systems
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-7 gap-2 bg-slate-100 p-1">
            <TabsTrigger value="tank-level" className="flex items-center gap-1">
              <Droplets className="h-4 w-4" />
              <span className="hidden sm:inline">Tank Level</span>
            </TabsTrigger>
            <TabsTrigger
              value="pump-energy"
              className="flex items-center gap-1"
            >
              <Zap className="h-4 w-4" />
              <span className="hidden sm:inline">Pump Energy</span>
            </TabsTrigger>
            <TabsTrigger value="valve-logs" className="flex items-center gap-1">
              <Power className="h-4 w-4" />
              <span className="hidden sm:inline">Valve Logs</span>
            </TabsTrigger>
            <TabsTrigger value="pump-hours" className="flex items-center gap-1">
              <Gauge className="h-4 w-4" />
              <span className="hidden sm:inline">Pump Hours</span>
            </TabsTrigger>
            <TabsTrigger value="pump-logs" className="flex items-center gap-1">
              <Power className="h-4 w-4" />
              <span className="hidden sm:inline">Pump Logs</span>
            </TabsTrigger>
            <TabsTrigger
              value="valve-duration"
              className="flex items-center gap-1"
            >
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Valve Duration</span>
            </TabsTrigger>
            <TabsTrigger value="water-flow" className="flex items-center gap-1">
              <ArrowUpDown className="h-4 w-4" />
              <span className="hidden sm:inline">Water Flow</span>
            </TabsTrigger>
          </TabsList>

          {/* TAB 1: Storage Asset Level */}
          <TabsContent value="tank-level" className="space-y-6">
            {/* Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Select Storage Asset</CardTitle>
                <CardDescription>
                  Choose a storage asset to view water level trends
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Storage Asset
                    </label>
                    <Select
                      value={selectedTank}
                      onValueChange={setSelectedTank}
                      disabled={loadingTanks || tanks.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingTanks ? "Loading..." : "Select storage asset"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {tanks.map((tank) => (
                          <SelectItem
                            key={tank.asset_key}
                            value={tank.asset_key}
                          >
                            {[
                              formatAssetDropdownLabel(
                                tank.name || tank.asset_name,
                                tank.asset_id || tank.tank_id,
                              ),
                              tank.asset_type_label,
                            ]
                              .filter(Boolean)
                              .join(" - ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter & Date Selection */}
            {selectedTank && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    Data Filter
                  </CardTitle>
                  <CardDescription>
                    Select time range and filter type for storage asset analysis
                    (All times in IST - Indian Standard Time)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Filter Type Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        View Data By:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="filterType"
                            value="daily"
                            checked={filterType === "daily"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm text-slate-700">Daily</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="filterType"
                            value="monthly"
                            checked={filterType === "monthly"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm text-slate-700">
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Daily Filter - Date Range */}
                    {filterType === "daily" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-slate-500">
                            Max 60 days back
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <p className="text-xs text-slate-500">
                            Today or earlier
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Monthly Filter - Month Selector */}
                    {filterType === "monthly" && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Month
                        </label>
                        <input
                          type="month"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="text-xs text-slate-500">
                          Current month and 2 previous months available
                        </p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyFilter}
                      disabled={loadingGraph}
                      className="w-full bg-blue-600 hover:bg-blue-700"
                    >
                      {loadingGraph ? "Loading..." : "Apply Filter & Load Data"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {tankInfo && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">
                        Storage Asset
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {tankInfo.name}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Capacity</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {tankInfo.capacity ? `${tankInfo.capacity}L` : "N/A"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Avg. Level</p>
                      <p className="text-2xl font-bold text-green-600">
                        {tankInfo.statistics?.average_percentage}%
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Data Points</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {tankInfo.dataPoints}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Graph Card */}
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-blue-600" />
                  {filterType === "daily"
                    ? "Daily Water Level Trend"
                    : "Monthly Water Level Trend"}
                </CardTitle>
                <CardDescription>
                  {graphData.length > 0
                    ? `Showing ${graphData.length} ${filterType === "daily" ? "data point" : "hourly"}${
                        graphData.length !== 1 ? "s" : ""
                      }`
                    : "No data available (Timestamps shown in IST)"}
                </CardDescription>
                {graphData.length > 0 && (
                  <ExportActions config={tankLevelExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {loadingGraph ? (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Loader className="h-12 w-12 text-blue-500 animate-spin" />
                    <p className="mt-4 text-slate-600">
                      Loading {filterType === "daily" ? "daily" : "monthly"}{" "}
                      data...
                    </p>
                  </div>
                ) : graphData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={graphData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <YAxis
                        domain={[0, 100]}
                        label={{
                          value: "Tank Percentage (%)",
                          angle: -90,
                          position: "insideLeft",
                          style: { fill: "#64748b" },
                        }}
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        wrapperStyle={{ paddingTop: "20px" }}
                        iconType="line"
                      />
                      <Line
                        type="monotone"
                        dataKey="percentage"
                        stroke="#3b82f6"
                        dot={{
                          fill: "#3b82f6",
                          r: 4,
                        }}
                        activeDot={{ r: 6 }}
                        strokeWidth={2}
                        name="Tank Percentage"
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Droplets className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {selectedTank
                        ? "No data available for this tank yet"
                        : "Select a storage asset to view data"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Data Table */}
            {/* {graphData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Hourly Data Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold text-slate-700">
                        Time
                      </th>
                      <th className="px-4 py-2 text-right font-semibold text-slate-700">
                        Percentage
                      </th>
                      <th className="px-4 py-2 text-right font-semibold text-slate-700">
                        Min
                      </th>
                      <th className="px-4 py-2 text-right font-semibold text-slate-700">
                        Max
                      </th>
                      <th className="px-4 py-2 text-right font-semibold text-slate-700">
                        Samples
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {graphData.map((row, idx) => (
                      <tr
                        key={idx}
                        className="border-t border-slate-200 hover:bg-slate-50"
                      >
                        <td className="px-4 py-2 text-slate-900">
                          {row.time}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                            {row.percentage}%
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right text-red-600">
                          {row.min_percentage}%
                        </td>
                        <td className="px-4 py-2 text-right text-green-600">
                          {row.max_percentage}%
                        </td>
                        <td className="px-4 py-2 text-right text-slate-600">
                          {row.sample_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )} */}
          </TabsContent>

          {/* TAB 2: Pump Energy Meter */}
          <TabsContent value="pump-energy" className="space-y-6">
            {/* Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Select Pump</CardTitle>
                <CardDescription>
                  Choose a pump to view energy meter data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  {/* Pump Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Pump
                    </label>
                    <Select
                      value={selectedPump}
                      onValueChange={setSelectedPump}
                      disabled={loadingPumps || pumps.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingPumps ? "Loading..." : "Select a pump"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {pumps.map((pump) => (
                          <SelectItem key={pump.pump_id} value={pump.pump_id}>
                            {formatAssetDropdownLabel(pump.name, pump.pump_id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Metric & Phase Selection */}
            {selectedPump && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-yellow-600" />
                    Metric & Phase Selection
                  </CardTitle>
                  <CardDescription>
                    Choose the metric and phase to display (All times in IST -
                    Indian Standard Time)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Metric Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        Energy Metric:
                      </label>
                      <div className="flex gap-4">
                        {[
                          { value: "voltage", label: "Voltage (V)" },
                          { value: "current", label: "Current (A)" },
                          { value: "pf", label: "Power Factor (%)" },
                        ].map((metric) => (
                          <label
                            key={metric.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="pumpMetric"
                              value={metric.value}
                              checked={pumpMetric === metric.value}
                              onChange={(e) => setPumpMetric(e.target.value)}
                              className="w-4 h-4 text-yellow-600"
                            />
                            <span className="text-sm text-slate-700">
                              {metric.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Phase Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        Phase:
                      </label>
                      <div className="flex gap-4">
                        {[
                          {
                            value: "r",
                            label: "Red (R)",
                            color: "text-red-600",
                          },
                          {
                            value: "y",
                            label: "Yellow (Y)",
                            color: "text-yellow-600",
                          },
                          {
                            value: "b",
                            label: "Blue (B)",
                            color: "text-blue-600",
                          },
                        ].map((phase) => (
                          <label
                            key={phase.value}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <input
                              type="radio"
                              name="pumpPhase"
                              value={phase.value}
                              checked={pumpPhase === phase.value}
                              onChange={(e) => setPumpPhase(e.target.value)}
                              className="w-4 h-4 text-yellow-600"
                            />
                            <span
                              className={`text-sm font-semibold ${phase.color}`}
                            >
                              {phase.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filter & Date Selection */}
            {selectedPump && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-yellow-600" />
                    Data Filter
                  </CardTitle>
                  <CardDescription>
                    Select time range and filter type for energy analysis (All
                    times in IST - Indian Standard Time)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Filter Type Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        View Data By:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="filterType"
                            value="daily"
                            checked={filterType === "daily"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-yellow-600"
                          />
                          <span className="text-sm text-slate-700">Daily</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="filterType"
                            value="monthly"
                            checked={filterType === "monthly"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-yellow-600"
                          />
                          <span className="text-sm text-slate-700">
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Daily Filter - Date Range */}
                    {filterType === "daily" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            max={getTodayIstDateString()}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                          <p className="text-xs text-slate-500">
                            Max 60 days back
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            max={getTodayIstDateString()}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          />
                          <p className="text-xs text-slate-500">
                            Today or earlier
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Monthly Filter - Month Selector */}
                    {filterType === "monthly" && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Month
                        </label>
                        <input
                          type="month"
                          value={month}
                          max={getTodayIstDateString().slice(0, 7)}
                          onChange={(e) => setMonth(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        />
                        <p className="text-xs text-slate-500">
                          Current month and 2 previous months available
                        </p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyPumpFilter}
                      disabled={pumpLoadingGraph}
                      className="w-full bg-yellow-600 hover:bg-yellow-700"
                    >
                      {pumpLoadingGraph
                        ? "Loading..."
                        : "Apply Filter & Load Data"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {pumpInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Pump ID</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {pumpInfo.pump_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Data Points</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {pumpInfo.dataPoints}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Metric</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {pumpMetric === "voltage"
                          ? "Voltage"
                          : pumpMetric === "current"
                            ? "Current"
                            : "Power Factor"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Graph Card */}
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600" />
                  {pumpMetric === "voltage"
                    ? "Voltage (R/Y/B)"
                    : pumpMetric === "current"
                      ? "Current (R/Y/B)"
                      : "Power Factor (R/Y/B)"}{" "}
                  Trend
                </CardTitle>
                <CardDescription>
                  {pumpGraphData.length > 0
                    ? `Showing ${pumpGraphData.length} ${
                        filterType === "daily" ? "data point" : "hourly"
                      }${pumpGraphData.length !== 1 ? "s" : ""}`
                    : "No data available (Timestamps shown in IST)"}
                </CardDescription>
                {pumpGraphData.length > 0 && (
                  <ExportActions config={pumpEnergyExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {pumpLoadingGraph ? (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Loader className="h-12 w-12 text-yellow-500 animate-spin" />
                    <p className="mt-4 text-slate-600">
                      Loading {filterType === "daily" ? "daily" : "monthly"}{" "}
                      pump energy data...
                    </p>
                  </div>
                ) : pumpGraphData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart
                      data={pumpGraphData}
                      margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="time"
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <YAxis
                        label={{
                          value:
                            pumpMetric === "voltage"
                              ? "Voltage (V)"
                              : pumpMetric === "current"
                                ? "Current (A)"
                                : "Power Factor",
                          angle: -90,
                          position: "insideLeft",
                          style: { fill: "#64748b" },
                        }}
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <Tooltip content={<PumpCustomTooltip />} />
                      <Legend
                        wrapperStyle={{ paddingTop: "20px" }}
                        iconType="line"
                      />
                      {pumpMetric === "voltage" && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="voltage_r"
                            stroke="#ef4444"
                            dot={{ fill: "#ef4444", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Red Phase (R)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="voltage_y"
                            stroke="#eab308"
                            dot={{ fill: "#eab308", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Yellow Phase (Y)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="voltage_b"
                            stroke="#3b82f6"
                            dot={{ fill: "#3b82f6", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Blue Phase (B)"
                            isAnimationActive={false}
                          />
                        </>
                      )}
                      {pumpMetric === "current" && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="current_r"
                            stroke="#ef4444"
                            dot={{ fill: "#ef4444", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Red Phase (R)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="current_y"
                            stroke="#eab308"
                            dot={{ fill: "#eab308", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Yellow Phase (Y)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="current_b"
                            stroke="#3b82f6"
                            dot={{ fill: "#3b82f6", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Blue Phase (B)"
                            isAnimationActive={false}
                          />
                        </>
                      )}
                      {pumpMetric === "pf" && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="pf_r"
                            stroke="#ef4444"
                            dot={{ fill: "#ef4444", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Red Phase (R)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="pf_y"
                            stroke="#eab308"
                            dot={{ fill: "#eab308", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Yellow Phase (Y)"
                            isAnimationActive={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="pf_b"
                            stroke="#3b82f6"
                            dot={{ fill: "#3b82f6", r: 3 }}
                            activeDot={{ r: 5 }}
                            strokeWidth={2}
                            name="Blue Phase (B)"
                            isAnimationActive={false}
                          />
                        </>
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Zap className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {!selectedPump
                        ? "Select a pump to view data"
                        : pumpInfo?.energyMeterConnected === false
                          ? "No data — the energy meter is not connected for this pump"
                          : "No energy meter data available for this pump"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: Valve On/Off Logs */}
          <TabsContent value="valve-logs" className="space-y-6">
            {/* Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Select Valve</CardTitle>
                <CardDescription>
                  Choose a valve to view state change logs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Valve
                    </label>
                    <Select
                      value={selectedValve}
                      onValueChange={setSelectedValve}
                      disabled={loadingValves || valves.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingValves ? "Loading..." : "Select a valve"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {valves.map((valve) => (
                          <SelectItem
                            key={valve.valve_id}
                            value={valve.valve_id}
                          >
                            {formatAssetDropdownLabel(
                              valve.name,
                              valve.valve_id,
                            )}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter & Date Selection */}
            {selectedValve && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    Data Filter
                  </CardTitle>
                  <CardDescription>
                    Select time range for valve state change analysis (All times
                    in IST)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Filter Type Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        View Data By:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="valveFilterType"
                            value="daily"
                            checked={filterType === "daily"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-purple-600"
                          />
                          <span className="text-sm text-slate-700">Daily</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="valveFilterType"
                            value="monthly"
                            checked={filterType === "monthly"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-purple-600"
                          />
                          <span className="text-sm text-slate-700">
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Daily Filter */}
                    {filterType === "daily" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          <p className="text-xs text-slate-500">
                            Max 60 days back
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                          <p className="text-xs text-slate-500">
                            Today or earlier
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Monthly Filter */}
                    {filterType === "monthly" && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Month
                        </label>
                        <input
                          type="month"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                        <p className="text-xs text-slate-500">
                          Current month and 2 previous months available
                        </p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyValveFilter}
                      disabled={valveLoadingLogs}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {valveLoadingLogs
                        ? "Loading..."
                        : "Apply Filter & Load Valve Logs"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {valveInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Valve ID</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {valveInfo.valve_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Device ID</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {valveInfo.device_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">
                        State Changes
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {valveInfo.stateChanges}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Valve Logs Table */}
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpDown className="h-5 w-5 text-purple-600" />
                  Valve State Change Logs
                </CardTitle>
                <CardDescription>
                  {valveLogData.length > 0
                    ? `Showing ${valveLogData.length} state change${valveLogData.length !== 1 ? "s" : ""} (Timestamps in IST)`
                    : "No data available (Timestamps shown in IST)"}
                </CardDescription>
                {valveLogData.length > 0 && (
                  <ExportActions config={valveLogsExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {valveLoadingLogs ? (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Loader className="h-12 w-12 text-purple-500 animate-spin" />
                    <p className="mt-4 text-slate-600">
                      Scanning packets for state changes...
                    </p>
                  </div>
                ) : valveLogData.length > 0 ? (
                  <div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-purple-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              #
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Timestamp (IST)
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700">
                              Valve State
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Valve ID
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Tank ID
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-700">
                              Tank %
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Assoc. Pumps
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {valveLogData
                            .slice(
                              (valveCurrentPage - 1) * valvePageSize,
                              valveCurrentPage * valvePageSize,
                            )
                            .map((row, idx) => (
                              <tr
                                key={idx}
                                className="border-t border-slate-200 hover:bg-slate-50 transition-colors"
                              >
                                <td className="px-4 py-3 text-slate-500 text-xs">
                                  {(valveCurrentPage - 1) * valvePageSize +
                                    idx +
                                    1}
                                </td>
                                <td className="px-4 py-3 text-slate-900 font-mono text-xs">
                                  {formatHumanDateTime(row.datetime)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                                      row.valve_state === "OPEN"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    <span
                                      className={`w-2 h-2 rounded-full ${
                                        row.valve_state === "OPEN"
                                          ? "bg-green-500"
                                          : "bg-red-500"
                                      }`}
                                    />
                                    {row.valve_state}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-slate-700 font-medium">
                                  {row.valve_id}
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {row.tank_id}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                                    {row.tank_percentage}%
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-slate-700">
                                  {formatAssociatedPumps(row) || "—"}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {valveLogData.length > valvePageSize && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm text-slate-600">
                          Page {valveCurrentPage} of{" "}
                          {Math.ceil(valveLogData.length / valvePageSize)} (
                          {valveLogData.length} total)
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setValveCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={valveCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setValveCurrentPage((p) =>
                                Math.min(
                                  Math.ceil(
                                    valveLogData.length / valvePageSize,
                                  ),
                                  p + 1,
                                ),
                              )
                            }
                            disabled={
                              valveCurrentPage ===
                              Math.ceil(valveLogData.length / valvePageSize)
                            }
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Power className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {selectedValve
                        ? "No state changes found for this valve in the selected range"
                        : "Select a valve, then apply a filter to view logs"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 4: Pump Running Hours */}
          <TabsContent value="pump-hours" className="space-y-6">
            {/* Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Select Pump</CardTitle>
                <CardDescription>
                  Choose a pump to view running hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  {/* Pump Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Pump
                    </label>
                    <Select
                      value={selectedPump}
                      onValueChange={setSelectedPump}
                      disabled={loadingPumps || pumps.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingPumps ? "Loading..." : "Select a pump"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {pumps.map((pump) => (
                          <SelectItem key={pump.pump_id} value={pump.pump_id}>
                            {formatAssetDropdownLabel(pump.name, pump.pump_id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter & Date Selection */}
            {selectedPump && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-600" />
                    Data Filter
                  </CardTitle>
                  <CardDescription>
                    Select time range for pump running hours analysis (All times
                    in IST)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Filter Type Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        View Data By:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="pumpHoursFilterType"
                            value="daily"
                            checked={filterType === "daily"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-teal-600"
                          />
                          <span className="text-sm text-slate-700">Daily</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="pumpHoursFilterType"
                            value="monthly"
                            checked={filterType === "monthly"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-teal-600"
                          />
                          <span className="text-sm text-slate-700">
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Daily Filter */}
                    {filterType === "daily" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            max={getTodayIstDateString()}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                          <p className="text-xs text-slate-500">
                            Max 60 days back
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            max={getTodayIstDateString()}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                          <p className="text-xs text-slate-500">
                            Today or earlier
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Monthly Filter */}
                    {filterType === "monthly" && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Month
                        </label>
                        <input
                          type="month"
                          value={month}
                          max={getTodayIstDateString().slice(0, 7)}
                          onChange={(e) => setMonth(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                        <p className="text-xs text-slate-500">
                          Current month and 2 previous months available
                        </p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyPumpHoursFilter}
                      disabled={pumpHoursLoadingLogs}
                      className="w-full bg-teal-600 hover:bg-teal-700"
                    >
                      {pumpHoursLoadingLogs
                        ? "Loading..."
                        : "Apply Filter & Load Running Hours"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {pumpHoursInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Pump ID</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {pumpHoursInfo.pump_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-teal-200 bg-teal-50">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-teal-700 mb-1">
                        Total Running Time
                      </p>
                      <p className="text-2xl font-bold text-teal-700">
                        {pumpHoursInfo.totalRunning}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">ON Sessions</p>
                      <p className="text-2xl font-bold text-green-600">
                        {pumpHoursInfo.sessionCount}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Bar Chart - Daily Running Hours */}
            {pumpHoursInfo &&
              pumpHoursInfo.dailyBreakdown &&
              pumpHoursInfo.dailyBreakdown.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-teal-600" />
                      Daily Running Hours
                    </CardTitle>
                    <CardDescription>
                      Total pump running time per day (in minutes)
                    </CardDescription>
                    <ExportActions config={pumpHoursExportConfig()} />
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={400}>
                      <BarChart
                        data={pumpHoursInfo.dailyBreakdown}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 12, fill: "#64748b" }}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: "#64748b" }}
                          label={{
                            value: "Minutes",
                            angle: -90,
                            position: "insideLeft",
                            style: { fill: "#64748b" },
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            borderRadius: "8px",
                            border: "1px solid #e2e8f0",
                          }}
                          formatter={(value, name) => {
                            const entry = pumpHoursInfo.dailyBreakdown.find(
                              (d) => d.total_minutes === value,
                            );
                            return [
                              entry ? entry.duration_formatted : `${value} min`,
                              "Running Time",
                            ];
                          }}
                          labelFormatter={(label) => `Date: ${label}`}
                        />
                        <Bar
                          dataKey="total_minutes"
                          fill="#14b8a6"
                          radius={[4, 4, 0, 0]}
                          name="Running Time (min)"
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
          </TabsContent>

          {/* TAB 5: Pump On/Off Logs */}
          <TabsContent value="pump-logs" className="space-y-6">
            {/* Controls */}
            <Card>
              <CardHeader>
                <CardTitle>Select Pump</CardTitle>
                <CardDescription>
                  Choose a pump to view state change logs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  {/* Pump Selection */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Pump
                    </label>
                    <Select
                      value={selectedPump}
                      onValueChange={setSelectedPump}
                      disabled={loadingPumps || pumps.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingPumps ? "Loading..." : "Select a pump"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {pumps.map((pump) => (
                          <SelectItem key={pump.pump_id} value={pump.pump_id}>
                            {formatAssetDropdownLabel(pump.name, pump.pump_id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Filter & Date Selection */}
            {selectedPump && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-orange-600" />
                    Data Filter
                  </CardTitle>
                  <CardDescription>
                    Select time range for pump state change analysis (All times
                    in IST)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Filter Type Selector */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium text-slate-700">
                        View Data By:
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="pumpLogsFilterType"
                            value="daily"
                            checked={filterType === "daily"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-orange-600"
                          />
                          <span className="text-sm text-slate-700">Daily</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="pumpLogsFilterType"
                            value="monthly"
                            checked={filterType === "monthly"}
                            onChange={(e) =>
                              handleFilterTypeChange(e.target.value)
                            }
                            className="w-4 h-4 text-orange-600"
                          />
                          <span className="text-sm text-slate-700">
                            Monthly
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Daily Filter */}
                    {filterType === "daily" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <p className="text-xs text-slate-500">
                            Max 60 days back
                          </p>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                          />
                          <p className="text-xs text-slate-500">
                            Today or earlier
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Monthly Filter */}
                    {filterType === "monthly" && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">
                          Select Month
                        </label>
                        <input
                          type="month"
                          value={month}
                          onChange={(e) => setMonth(e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <p className="text-xs text-slate-500">
                          Current month and 2 previous months available
                        </p>
                      </div>
                    )}

                    {/* Apply Button */}
                    <Button
                      onClick={handleApplyPumpLogsFilter}
                      disabled={pumpLogsLoading}
                      className="w-full bg-orange-600 hover:bg-orange-700"
                    >
                      {pumpLogsLoading
                        ? "Loading..."
                        : "Apply Filter & Load Pump Logs"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            {pumpLogsInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Pump ID</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {pumpLogsInfo.pump_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">Device ID</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {pumpLogsInfo.device_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-600 mb-1">
                        State Changes
                      </p>
                      <p className="text-2xl font-bold text-green-600">
                        {pumpLogsInfo.stateChanges}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Pump Logs Table */}
            <Card className="col-span-full">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpDown className="h-5 w-5 text-orange-600" />
                  Pump State Change Logs
                </CardTitle>
                <CardDescription>
                  {pumpLogData.length > 0
                    ? `Showing ${pumpLogData.length} state change${pumpLogData.length !== 1 ? "s" : ""} (Timestamps in IST)`
                    : "No data available (Timestamps shown in IST)"}
                </CardDescription>
                {pumpLogData.length > 0 && (
                  <ExportActions config={pumpLogsExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {pumpLogsLoading ? (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Loader className="h-12 w-12 text-orange-500 animate-spin" />
                    <p className="mt-4 text-slate-600">
                      Scanning packets for pump state changes...
                    </p>
                  </div>
                ) : pumpLogData.length > 0 ? (
                  <div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-orange-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              #
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Timestamp (IST)
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700">
                              Pump State
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              Pump ID
                            </th>
                            <th className="px-4 py-3 text-center font-semibold text-slate-700">
                              Health
                            </th>
                            {/* <th className="px-4 py-3 text-left font-semibold text-slate-700">
                              UG Tank
                            </th>
                            <th className="px-4 py-3 text-right font-semibold text-slate-700">
                              UG Level
                            </th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {pumpLogData
                            .slice(
                              (pumpLogsCurrentPage - 1) * pumpLogsPageSize,
                              pumpLogsCurrentPage * pumpLogsPageSize,
                            )
                            .map((row, idx) => (
                              <tr
                                key={idx}
                                className="border-t border-slate-200 hover:bg-slate-50 transition-colors"
                              >
                                <td className="px-4 py-3 text-slate-500 text-xs">
                                  {(pumpLogsCurrentPage - 1) *
                                    pumpLogsPageSize +
                                    idx +
                                    1}
                                </td>
                                <td className="px-4 py-3 text-slate-900 font-mono text-xs">
                                  {formatHumanDateTime(row.datetime)}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                                      row.pump_state === "ON"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-red-100 text-red-800"
                                    }`}
                                  >
                                    <span
                                      className={`w-2 h-2 rounded-full ${
                                        row.pump_state === "ON"
                                          ? "bg-green-500"
                                          : "bg-red-500"
                                      }`}
                                    />
                                    {row.pump_state}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-slate-700 font-medium">
                                  {row.pump_id}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <span
                                    className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                                      row.pump_health === "no_fault"
                                        ? "bg-green-100 text-green-700"
                                        : "bg-yellow-100 text-yellow-700"
                                    }`}
                                  >
                                    {row.pump_health || "—"}
                                  </span>
                                </td>
                                {/* <td className="px-4 py-3 text-slate-700">
                                  {row.ugt_id || "—"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {row.ugt_level != null ? (
                                    <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold">
                                      {row.ugt_level}%
                                    </span>
                                  ) : (
                                    "—"
                                  )}
                                </td> */}
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {pumpLogData.length > pumpLogsPageSize && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm text-slate-600">
                          Page {pumpLogsCurrentPage} of{" "}
                          {Math.ceil(pumpLogData.length / pumpLogsPageSize)} (
                          {pumpLogData.length} total)
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPumpLogsCurrentPage((p) => Math.max(1, p - 1))
                            }
                            disabled={pumpLogsCurrentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPumpLogsCurrentPage((p) =>
                                Math.min(
                                  Math.ceil(
                                    pumpLogData.length / pumpLogsPageSize,
                                  ),
                                  p + 1,
                                ),
                              )
                            }
                            disabled={
                              pumpLogsCurrentPage ===
                              Math.ceil(pumpLogData.length / pumpLogsPageSize)
                            }
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Power className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {selectedPump
                        ? "No state changes found for this pump in the selected range"
                        : "Select a pump, then apply a filter to view logs"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 6: Valve On Duration */}
          <TabsContent value="valve-duration" className="space-y-6">
            {/* Tower & Valve Selector */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Droplets className="h-5 w-5 text-indigo-600" />
                  Select Valve
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Valve
                    </label>
                    <Select
                      value={selectedValve}
                      onValueChange={(val) => {
                        setSelectedValve(val);
                        setValveDurationData(null);
                        setValveDurationInfo(null);
                      }}
                      disabled={loadingValves || valves.length === 0}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingValves ? "Loading..." : "Select valve"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {valves.map((v) => (
                          <SelectItem key={v.valve_id} value={v.valve_id}>
                            {formatAssetDropdownLabel(v.name, v.valve_id)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date Filter */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-indigo-600" />
                  Date Filter
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">
                      Filter Type
                    </label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {filterType === "daily" ? (
                    <>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          max={getTodayIstDateString()}
                          onChange={(e) => setStartDate(e.target.value)}
                          className="border rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          max={getTodayIstDateString()}
                          onChange={(e) => setEndDate(e.target.value)}
                          className="border rounded-md px-3 py-2 text-sm"
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">
                        Month
                      </label>
                      <input
                        type="month"
                        value={month}
                        max={getTodayIstDateString().slice(0, 7)}
                        onChange={(e) => setMonth(e.target.value)}
                        className="border rounded-md px-3 py-2 text-sm"
                      />
                    </div>
                  )}
                  <Button
                    onClick={handleApplyValveDurationFilter}
                    disabled={!selectedValve || valveDurationLoading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {valveDurationLoading ? (
                      <Loader className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Apply Filter
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Cards */}
            {valveDurationInfo && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Valve ID</p>
                      <p className="text-xl font-bold text-indigo-600">
                        {valveDurationInfo.valve_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Device ID</p>
                      <p className="text-xl font-bold text-slate-800">
                        {valveDurationInfo.device_id}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">OPEN Sessions</p>
                      <p className="text-xl font-bold text-green-600">
                        {valveDurationInfo.session_count}
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-slate-500">Total Open Time</p>
                      <p className="text-xl font-bold text-indigo-600">
                        {valveDurationInfo.total_open}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-indigo-600" />
                  Valve Open Duration
                </CardTitle>
                <CardDescription>
                  Daily total open time (in minutes) - bar chart
                </CardDescription>
                {valveDurationData?.sessions?.length > 0 && (
                  <ExportActions config={valveDurationExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {valveDurationLoading ? (
                  <div className="flex items-center justify-center h-96">
                    <Loader className="h-8 w-8 animate-spin text-indigo-600" />
                  </div>
                ) : valveDurationData &&
                  valveDurationData.daily_breakdown &&
                  valveDurationData.daily_breakdown.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={valveDurationData.daily_breakdown}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#64748b" }}
                        label={{
                          value: "Minutes",
                          angle: -90,
                          position: "insideLeft",
                          style: { fill: "#64748b" },
                        }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                        formatter={(value, name) => {
                          const entry = valveDurationData.daily_breakdown.find(
                            (d) => d.total_minutes === value,
                          );
                          return [
                            entry ? entry.duration_formatted : `${value} min`,
                            "Open Duration",
                          ];
                        }}
                        labelFormatter={(label) => `Date: ${label}`}
                      />
                      <Bar
                        dataKey="total_minutes"
                        fill="#6366f1"
                        radius={[4, 4, 0, 0]}
                        name="Open Duration (min)"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Clock className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {selectedValve
                        ? "No open duration data found for this valve in the selected range"
                        : "Select a valve, then apply a filter to view duration"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 7: Water Flow */}
          <TabsContent value="water-flow" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpDown className="h-5 w-5 text-cyan-600" />
                  Select Storage Asset
                </CardTitle>
                <CardDescription>
                  Calculate inflow and outflow from storage asset level movement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      Storage Asset
                    </label>
                    <Select
                      value={selectedTank}
                      onValueChange={(value) => {
                        setSelectedTank(value);
                        setWaterFlowData([]);
                        setWaterFlowInfo(null);
                      }}
                      disabled={loadingTanks || tanks.length === 0}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue
                          placeholder={
                            loadingTanks ? "Loading..." : "Select storage asset"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {tanks.map((tank) => (
                          <SelectItem
                            key={tank.asset_key}
                            value={tank.asset_key}
                          >
                            {[
                              formatAssetDropdownLabel(
                                tank.name || tank.asset_name,
                                tank.asset_id || tank.tank_id,
                              ),
                              tank.asset_type_label,
                            ]
                              .filter(Boolean)
                              .join(" - ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-cyan-600" />
                  Water Flow Filter
                </CardTitle>
                <CardDescription>
                  Usage details are calculated from historical packets and small
                  fluctuations are ignored.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                  <div className="flex flex-wrap gap-3">
                    {[
                      { value: "daily", label: "Day" },
                      { value: "monthly", label: "Month" },
                      { value: "average", label: "Avg" },
                    ].map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        variant={
                          waterFlowView === option.value ? "default" : "outline"
                        }
                        onClick={() => {
                          setWaterFlowView(option.value);
                          setWaterFlowData([]);
                          setWaterFlowInfo(null);
                        }}
                        className={
                          waterFlowView === option.value
                            ? "bg-cyan-600 hover:bg-cyan-700"
                            : ""
                        }
                      >
                        {option.label}
                      </Button>
                    ))}
                  </div>

                  {waterFlowView === "daily" ? (
                    <input
                      type="date"
                      aria-label="Water flow date"
                      value={waterFlowDate}
                      max={getTodayIstDateString()}
                      onChange={(e) => setWaterFlowDate(e.target.value)}
                      className="h-10 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 lg:w-72"
                    />
                  ) : (
                    <input
                      type="month"
                      aria-label="Water flow month"
                      value={waterFlowMonth}
                      max={getTodayIstDateString().slice(0, 7)}
                      onChange={(e) => setWaterFlowMonth(e.target.value)}
                      className="h-10 w-full rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500 lg:w-72"
                    />
                  )}

                  <Button
                    onClick={handleApplyWaterFlowFilter}
                    disabled={!selectedTank || waterFlowLoading}
                    className="h-10 w-full bg-cyan-600 hover:bg-cyan-700 lg:w-72"
                  >
                    {waterFlowLoading
                      ? "Calculating..."
                      : "Calculate Water Flow"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {waterFlowInfo && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-slate-500">Tank</p>
                    <p className="text-xl font-bold text-slate-900">
                      {waterFlowInfo.tankName || waterFlowInfo.tankId}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-blue-700">Inflow</p>
                    <p className="text-xl font-bold text-blue-700">
                      {formatWaterVolume(waterFlowInfo.summary?.total_inflow_l)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-pink-200 bg-pink-50">
                  <CardContent className="pt-6 text-center">
                    <p className="text-sm text-pink-700">Outflow</p>
                    <p className="text-xl font-bold text-pink-700">
                      {formatWaterVolume(
                        waterFlowInfo.summary?.total_outflow_l,
                      )}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ArrowUpDown className="h-5 w-5 text-cyan-600" />
                  {waterFlowView === "average"
                    ? "Average Hourly Water Flow"
                    : waterFlowView === "monthly"
                      ? "Monthly Water Flow"
                      : "Hourly Water Flow"}
                </CardTitle>
                <CardDescription>
                  Blue bars show inflow and pink bars show outflow.
                </CardDescription>
                {waterFlowData.length > 0 && (
                  <ExportActions config={waterFlowExportConfig()} />
                )}
              </CardHeader>
              <CardContent>
                {waterFlowLoading ? (
                  <div className="flex flex-col items-center justify-center h-96">
                    <Loader className="h-12 w-12 text-cyan-500 animate-spin" />
                    <p className="mt-4 text-slate-600">
                      Calculating water movement...
                    </p>
                  </div>
                ) : waterFlowData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={420}>
                    <BarChart
                      data={waterFlowData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "#64748b" }}
                      />
                      <YAxis
                        tick={{ fontSize: 12, fill: "#64748b" }}
                        tickFormatter={(value) =>
                          `${formatFixedNumber(Math.abs(Number(value)), 3)} KL`
                        }
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "8px",
                          border: "1px solid #e2e8f0",
                        }}
                        labelFormatter={(label, payload) => {
                          const row = payload?.[0]?.payload;
                          return row?.datetime
                            ? formatHumanDateTime(row.datetime)
                            : label;
                        }}
                        formatter={(value, name) => {
                          const label =
                            name === "inflow_kl" ? "Inflow" : "Outflow";
                          return [
                            `${formatFixedNumber(Math.abs(Number(value)), 3)} KL`,
                            label,
                          ];
                        }}
                      />
                      <Legend
                        formatter={(value) =>
                          value === "inflow_kl" ? "Inflow" : "Outflow"
                        }
                      />
                      <Bar
                        dataKey="inflow_kl"
                        fill="#2f96db"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        dataKey="outflow_negative_kl"
                        fill="#f70b57"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-96">
                    <ArrowUpDown className="h-12 w-12 text-slate-300 mb-4" />
                    <p className="text-slate-500">
                      {selectedTank
                        ? "Apply a filter to calculate water flow"
                        : "Select a storage asset to view water flow"}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default TankPercentageGraph;
